/**
 * 水库水情 (sksq) 组件专用数据筛选工具
 * 根据adcd值筛选水库数据，筛选大型水库 (SKTYPE == "dx")
 */

// 长江相关水库测站STCD列表
const CJ_STCD_LIST = [
  "61103450", "61105650", "61108050", "61113550", "61201300",
  "61312550", "61302350", "61401900", "61402350", "60106980"
];

// 水系映射关系
const HNNM_MAP = {
  '1': '湘江',
  '2': '资水',
  '3': '沅',
  '4': '澧水',
  '5': '洞庭湖',
  '6': '长江',
  '7': '珠江'
};

/**
 * 根据adcd值筛选水库数据
 * @param {Array} data - 原始水库数据数组
 * @param {String} adcd - 行政区划/筛选代码
 * @param {String} name - 搜索关键词（可选）
 * @returns {Array} 筛选后的数据
 */
export function filterRvsrData(data, adcd, name = '') {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  let result = [];

  // 处理adcd长度为3的情况，补0
  let processedAdcd = adcd;
  if (adcd && adcd.length === 3) {
    processedAdcd = adcd + '0';
  }

  // 1. adcd为单个数字（1-7）- 按水系筛选
  if (processedAdcd && processedAdcd.length === 1 && processedAdcd !== '0') {
    if (processedAdcd === '6') {
      result = data.filter(item => {
        const matchCj = CJ_STCD_LIST.includes(item.STCD);
        const matchName = matchKeyword(item, name);
        return matchCj && matchName;
      });
    } else {
      const hnnm = HNNM_MAP[processedAdcd];
      result = data.filter(item => {
        const matchHnnm = hnnm && item.HNNM && item.HNNM.includes(hnnm);
        const matchName = matchKeyword(item, name);
        return matchHnnm && matchName;
      });
    }
  }
  // 2. adcd为'0' - 筛选STCD以611-615开头的数据
  else if (processedAdcd === '0') {
    result = data.filter(item => {
      const stcd = item.STCD || '';
      const matchStcd = /^611|^612|^613|^614|^615/.test(stcd);
      const matchName = matchKeyword(item, name);
      return matchStcd && matchName;
    });
  }
  // 3. adcd为'qb'（全部）- 返回所有数据
  else if (processedAdcd === 'qb') {
    result = data.filter(item => {
      const matchName = matchKeyword(item, name);
      return matchName;
    });
  }
  // 4. adcd为'cj'（长江）- 按特定STCD列表筛选
  else if (processedAdcd === 'cj') {
    result = data.filter(item => {
      const matchCj = CJ_STCD_LIST.includes(item.STCD);
      const matchName = matchKeyword(item, name);
      return matchCj && matchName;
    });
  }
  // 5. 其他情况 - 按ADDVCD前缀筛选
  else if (processedAdcd) {
    const regx = new RegExp('^' + processedAdcd);
    result = data.filter(item => {
      const matchAddvcd = item.ADDVCD && regx.test(item.ADDVCD);
      const matchName = matchKeyword(item, name);
      return matchAddvcd && matchName;
    });
  }
  else {
    result = data;
  }

  return result;
}

/**
 * 筛选大型水库（SKTYPE 等于 "dx"）
 * @param {Array} data - 水库数据数组
 * @returns {Array} 大型水库数据
 */
export function filterLargeRvsr(data) {
  if (!data || !Array.isArray(data)) {
    return [];
  }
  return data.filter(item => {
    return item.SKTYPE === 'dx';
  });
}

/**
 * 添加水系排名字段 HN_NUM
 * @param {Array} data - 水库数据数组
 * @returns {Array} 添加了HN_NUM的数据
 */
export function addHnnmRank(data) {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map(item => {
    const hnnm = (item.HNNM || '').trim();
    let hnNum = 8;

    if (hnnm === '湘江') {
      hnNum = 1;
    } else if (hnnm === '资水') {
      hnNum = 2;
    } else if (hnnm === '沅水' || hnnm === '沅江' || hnnm === '沅水区') {
      hnNum = 3;
    } else if (hnnm === '澧水') {
      hnNum = 4;
    } else if (hnnm.indexOf('洞庭湖') > -1) {
      hnNum = 5;
    } else if (hnnm.indexOf('长江') > -1) {
      hnNum = 6;
    } else if (hnnm === '珠江') {
      hnNum = 7;
    }

    return { ...item, HN_NUM: hnNum };
  });
}

/**
 * 按水系排名和比汛限排序
 * @param {Array} data - 水库数据数组
 * @returns {Array} 排序后的数据
 */
export function sortRvsrData(data) {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.sort((a, b) => {
    const bxxA = parseFloat(a.BXX);
    const bxxB = parseFloat(b.BXX);
    const hnNumA = a.HN_NUM || 8;    const hnNumB = b.HN_NUM || 8;

    if ((bxxA > 0 && bxxB > 0) || (bxxA === bxxB) || (bxxA < 0 && bxxB < 0) || (isNaN(bxxA) && isNaN(bxxB))) {
      if (hnNumA < hnNumB) {
        return -1;
      } else if (hnNumA === hnNumB) {
        if (!isNaN(bxxA) && !isNaN(bxxB) && bxxA > bxxB) {
          return -1;
        }
      }
    } else if (bxxA > bxxB) {
      return -1;
    }
    return 1;
  });
}

/**
 * 匹配关键词（站名或地址）
 */
function matchKeyword(item, keyword) {
  if (!keyword || keyword.trim() === '') {
    return true;
  }
  const stnm = (item.STNM || '').toLowerCase();
  const stlc = (item.STLC || '').toLowerCase();
  const search = keyword.toLowerCase();
  return stnm.includes(search) || stlc.includes(search);
}

/**
 * 格式化水库数据
 * @param {Array} data - 原始水库数据
 * @returns {Array} 格式化后的数据
 */
export function formatRvsrData(data) {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map(item => {
    const formatted = { ...item };

    if (formatted.RZ) {
      formatted.RZ = parseFloat(formatted.RZ).toFixed(2);
    }

    if (formatted.INQ) {
      formatted.INQ = parseFloat(formatted.INQ).toFixed(2);
    } else {
      formatted.INQ = '-';
    }

    if (formatted.OTQ) {
      formatted.OTQ = parseFloat(formatted.OTQ).toFixed(2);
    } else {
      formatted.OTQ = '-';
    }

    if (formatted.RZ && formatted.FSLTDZ) {
      formatted.BXX = parseFloat(formatted.RZ - formatted.FSLTDZ).toFixed(2);
    } else {
      formatted.BXX = '-';
    }

    if (formatted.STNM) {
      formatted.STNM = formatted.STNM.trim();
    }

    return formatted;
  });
}

/**
 * 获取接口所需的adcd参数
 * @param {String} adcd - 前端筛选码
 * @returns {String} 接口所需的adcd参数
 */
export function getApiAdcd(adcd) {
  const specialCodes = ['qb', 'cj', '0', '1', '2', '3', '4', '5', '6', '7'];
  if (specialCodes.includes(adcd)) {
    return '';
  }
  return adcd || '';
}

/**
 * 是否需要前端筛选
 * @param {String} adcd - 前端筛选码
 * @returns {Boolean} 是否需要前端筛选
 */
export function needFrontendFilter(adcd) {
  const frontendCodes = ['qb', 'cj', '0', '1', '2', '3', '4', '5', '6', '7'];
  return frontendCodes.includes(adcd);
}

/**
 * 综合处理水库数据（筛选+排序+格式化）
 * @param {Array} data - 原始数据
 * @param {String} adcd - 行政区划代码
 * @param {String} name - 搜索关键词
 * @returns {Array} 处理后的数据
 */
export function processRvsrData(data, adcd, name = '') {
  // 1. 筛选水库数据
  let result = filterRvsrData(data, adcd, name);
  // 2. 筛选大型水库
  result = filterLargeRvsr(result);
  // 3. 添加水系排名
  result = addHnnmRank(result);
  // 4. 格式化数据
  result = formatRvsrData(result);
  // 5. 排序
  result = sortRvsrData(result);

  return result;
}

export default {
  filterRvsrData,
  filterLargeRvsr,
  addHnnmRank,
  sortRvsrData,
  formatRvsrData,
  processRvsrData,
  getApiAdcd,
  needFrontendFilter
};



// WEBPACK FOOTER //
// ./src/utils/sksqFilter.js